import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './Projects.css';
import '../Animation.css';
import ProjectModal from '../../components/ProjectModal/ProjectModal';
import { getProjects, deleteProject } from '../../api/api';
import { useAuthStore } from '../../store/authStore';
import Loader from '../../components/Loader/Loader';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (selectedProject || isEditingMode) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [selectedProject, isEditingMode]);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const data = await getProjects();
        setProjects(data);
      } catch (err) {
        console.error('Błąd pobierania projektów:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const openProjectModal = (project = null, edit = false) => {
    setSelectedProject(project);
    setIsEditingMode(edit);
  };

  const closeModal = () => {
    setSelectedProject(null);
    setIsEditingMode(false);
  };

  const handleProjectSaved = (savedProject) => {
    setProjects((prev) => {
      if (savedProject.id) {
        return prev.map((p) => (p.id === savedProject.id ? savedProject : p));
      }
      return [...prev, savedProject];
    });
    setSelectedProject(savedProject);
    setIsEditingMode(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Na pewno chcesz usunąć ten projekt?')) return;
    try {
      await deleteProject(id);
      setProjects((prev) => prev.filter((p) => p.id !== id));
      if (selectedProject?.id === id) closeModal();
    } catch (err) {
      console.error('Błąd usuwania projektu:', err);
      alert('Nie udało się usunąć projektu');
    }
  };

  if (loading) return <Loader />;

  return (
    <>
      <Helmet>
        <html lang="pl" />
        <title>
          Projekty - Portfolio Mikołaj Melnyk | Web Developer React & Full-Stack
        </title>
        <meta
          name="description"
          content="Przegląd moich projektów programistycznych – nowoczesne aplikacje webowe, strony responsywne, panele administracyjne i rozwiązania full-stack tworzone w React, Next.js, Node.js, Express, Tailwind CSS i PostgreSQL. Portfolio junior web developera."
        />
        <meta
          name="keywords"
          content="projekty, portfolio, web developer, React, Next.js, Node.js, fullstack, frontend, Tailwind CSS, PostgreSQL, aplikacje webowe, Rzeszów, programista"
        />
        <meta
          property="og:title"
          content="Projekty - Portfolio Mikołaj Melnyk | React • Node.js • Full-Stack"
        />
        <meta
          property="og:description"
          content="Sprawdź moje realizacje – od responsywnych stron i landing page po aplikacje z backendem i bazami danych. Chętnie podejmę nowe wyzwania!"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mikolajmelnyk.pl/Projects" />
        <meta property="og:locale" content="pl_PL" />
        <meta property="og:site_name" content="Portfolio Mikołaj Melnyk" />

        {/* Dodatkowe dla lepszego SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Mikołaj Melnyk" />
      </Helmet>

      <div className="projects-container">
        <h1 className="title_h1">Moje projekty</h1>

        <div className="projects-intro">
          <p>
            Poniżej znajdziesz wybrane projekty, które realizowałem zarówno
            samodzielnie, jak i w ramach nauki oraz pracy zawodowej. Każdy z
            nich wykorzystuje nowoczesny stack technologii, dostosowany do
            konkretnego celu i wymagań.
          </p>
          <p>
            Jeśli potrzebujesz szczegółowych informacji, kodu źródłowego, demo
            lub chcesz omówić potencjalną współpracę –
            <strong> serdecznie zapraszam do kontaktu</strong> przez formularz
            lub LinkedIn!
          </p>
        </div>

        <h3>Niektóre projekty znajdziesz również na GitHub</h3>
        <div className="social-icon-git">
          <ul>
            <li>
              <a
                href="https://github.com/Mikolaj1921"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa-brands fa-github"></i>
              </a>
            </li>
          </ul>
        </div>

        {isAuthenticated && (
          <button
            className="add-project-btn"
            onClick={() => openProjectModal(null, true)}
          >
            <i className="fas fa-plus"></i> Dodaj nowy projekt
          </button>
        )}

        <div className="section-projects">
          {projects.length === 0 ? (
            <p
              style={{ textAlign: 'center', color: '#888', fontSize: '1.1rem' }}
            >
              Brak projektów do wyświetlenia
            </p>
          ) : (
            projects.map((project) => (
              <div key={project.id} className="pr-card">
                <div className="pt-img">
                  {project.mainImageUrl ? (
                    <img src={project.mainImageUrl} alt={project.title} />
                  ) : (
                    <div className="no-image">Brak zdjęcia</div>
                  )}
                  <button
                    onClick={() => openProjectModal(project, false)}
                    className="details-btn"
                  >
                    Szczegóły
                  </button>
                </div>

                <div className="col-text">
                  <div className="pr-name">{project.title}</div>

                  {project.stack?.length > 0 && (
                    <div className="project-stack">
                      <strong>Stack:</strong> {project.stack.join(' • ')}
                    </div>
                  )}

                  {isAuthenticated && (
                    <div className="admin-actions">
                      <button
                        onClick={() => openProjectModal(project, true)}
                        className="edit-btn"
                      >
                        Edytuj
                      </button>
                      <button
                        onClick={() => handleDelete(project.id)}
                        className="delete-btn"
                      >
                        Usunąć
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {(selectedProject || isEditingMode) && (
        <ProjectModal
          project={selectedProject}
          onClose={closeModal}
          isEditing={isEditingMode}
          onSave={handleProjectSaved}
          isAuthenticated={isAuthenticated}
        />
      )}
    </>
  );
};

export default Projects;
